/**
 * Add/Edit Resource dialog (DNKADM-0054 U1) — a MUI `Dialog` that creates/updates a
 * `v2_resource` entity via `createResource` / `updateResource` and round-trips its
 * **location assignments** via `getResourceLocations` / `setResourceLocations` (handoff
 * §5.7).
 *
 * Fields: **name** (required — unique on `(tenant, name)`), **description** (optional),
 * **capacity** (required integer ≥ 1 — the max simultaneous reservations, §5.7), and a
 * **multi-select of the tenant's active owned locations** (the locations the resource
 * can be scheduled at). `isActive` is NOT set here — activation is the list's
 * deactivate/reactivate affordance (§4.3).
 *
 * On create: POST the resource, then (if any locations were picked) PUT the assignments
 * for the new id. On edit: PUT the resource, then PUT the assignments if they changed.
 * A validation `400` / `409` renders INLINE; anything else raises an error toast
 * (`classifyFormError`). The active-locations list loads via a plain inline fetch
 * (an empty tenant simply shows no options with a helper note) — mirrors `StaffDialog`.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createResource,
  getResourceLocations,
  setResourceLocations,
  updateResource,
  type ResourceInput,
  type V2Resource,
} from '@/api/resources';
import { listLocations, type V2Location } from '@/api/locations';
import type { ShowToast } from '@/components/toastContext';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { classifyFormError } from '@/api/screenError';

export interface ResourceDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The resource being edited, or `null` to add a new one. */
  resource: V2Resource | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  capacity?: string;
}

export function ResourceDialog(props: ResourceDialogProps) {
  // Remount the inner form per open / per edited resource so its inputs reseed.
  if (!props.open) return null;
  return <ResourceDialogForm key={props.resource?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function ResourceDialogForm({
  resource,
  onClose,
  onSaved,
  showToast,
}: Omit<ResourceDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = resource !== null;

  const [name, setName] = useState(resource?.name ?? '');
  const [description, setDescription] = useState(resource?.description ?? '');
  // Capacity is edited as text so a transient empty/partial value is allowed; it is
  // validated to an integer ≥ 1 on save. Defaults to 1 for a new resource.
  const [capacity, setCapacity] = useState(String(resource?.capacity ?? 1));

  // The tenant's active owned locations (the assignable set) + the picked ids.
  const [locations, setLocations] = useState<V2Location[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  // The assignments as loaded for an edit (to detect a change and skip a no-op PUT).
  const [initialLocationIds, setInitialLocationIds] = useState<string[] | null>(
    isEdit ? null : [],
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The plan-limit popup payload (DNKADM-0065) when a create is capped; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  // Load the active owned locations once; on edit, also load the current assignments.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [locs, assigned] = await Promise.all([
          listLocations({ status: 'active' }),
          isEdit && resource
            ? getResourceLocations(resource.id)
            : Promise.resolve<string[]>([]),
        ]);
        if (cancelled) return;
        setLocations(locs);
        // Keep only assignments that are still active+known (a stale id is dropped
        // from the control but won't be re-sent).
        const known = new Set(locs.map((l) => l.id));
        const kept = assigned.filter((id) => known.has(id));
        setSelectedLocationIds(kept);
        setInitialLocationIds(assigned);
      } catch {
        if (cancelled) return;
        // A locations/assignments load failure is non-fatal: the resource fields still
        // save; the assignment control just shows no options + a note.
        setLocations([]);
        setInitialLocationIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, resource]);

  function validate(): ResourceInput | null {
    const errors: FieldErrors = {};
    const tName = name.trim();
    if (tName === '') errors.name = 'Enter a name.';
    const cap = Number(capacity.trim());
    if (!Number.isInteger(cap) || cap < 1) {
      errors.capacity = 'Capacity must be a whole number of 1 or more.';
    }
    if (errors.name || errors.capacity) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: ResourceInput = { name: tName, capacity: cap };
    const d = optional(description);
    if (d !== undefined) input.description = d;
    return input;
  }

  /** Whether the location selection differs from what was loaded (skip a no-op PUT). */
  function locationsChanged(): boolean {
    if (initialLocationIds === null) return false; // assignments not loaded yet
    if (initialLocationIds.length !== selectedLocationIds.length) return true;
    const initial = new Set(initialLocationIds);
    return selectedLocationIds.some((id) => !initial.has(id));
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      const saved =
        isEdit && resource
          ? await updateResource(resource.id, input)
          : await createResource(input);
      const id = saved?.id ?? resource?.id ?? null;
      // Persist the location assignments when they changed (or always on create with
      // a non-empty selection), once we have the resource id.
      if (id !== null && (locationsChanged() || (!isEdit && selectedLocationIds.length > 0))) {
        await setResourceLocations(id, selectedLocationIds);
      }
      // DNKADM-0320: confirm the save (after every write resolves; one toast per action).
      showToast(isEdit ? 'Resource saved' : 'Resource added', 'success');
      onSaved();
    } catch (err) {
      // Plan-limit enforcement is Add-only (DNKADM-0065 / DNKAPI-0139) — Edit is
      // never blocked, so only a create maps these codes.
      if (!isEdit) {
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — show the upgrade popup (NOT a generic toast).
          setPlanLimit(parsePlanLimit(err));
          return;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup); the
          // admin can resubmit (the create may well be under the cap).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return;
        }
      }
      const view = classifyFormError(
        err,
        'The resource couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const locationsLabelId = `${ids}-locations-label`;

  return (
    <>
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit resource' : 'Add resource'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <TextField
            label="Capacity"
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            disabled={saving}
            size="small"
            required
            error={Boolean(fieldErrors.capacity)}
            helperText={
              fieldErrors.capacity ??
              'Max simultaneous reservations on this resource (1 or more).'
            }
            sx={{ width: 220 }}
            slotProps={{
              htmlInput: { 'aria-label': 'Capacity', min: 1, step: 1, inputMode: 'numeric' },
            }}
          />

          <FormControl size="small" fullWidth disabled={saving || locations.length === 0}>
            <InputLabel id={locationsLabelId}>Assigned locations</InputLabel>
            <Select
              labelId={locationsLabelId}
              multiple
              value={selectedLocationIds}
              onChange={(e) =>
                setSelectedLocationIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                )
              }
              input={<OutlinedInput label="Assigned locations" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const loc = locations.find((l) => l.id === id);
                    return (
                      <Chip key={id} size="small" label={loc ? loc.name || loc.id : id} />
                    );
                  })}
                </Box>
              )}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  <Checkbox checked={selectedLocationIds.includes(loc.id)} size="small" />
                  <ListItemText primary={loc.name === '' ? loc.id : loc.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {locations.length === 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1.5 }}>
              No active locations to assign yet. Add a location first, then assign this
              resource to it.
            </Typography>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save resource' : 'Add resource'}
        </Button>
      </DialogActions>
    </Dialog>
    <PlanLimitDialog
      open={planLimit !== null}
      info={planLimit}
      onClose={() => setPlanLimit(null)}
    />
    </>
  );
}
